import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { normalizeTestNumber } from '../utils/botTestMode';
import { lastWeekRange } from '../utils/ownerReportPage';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

export const isMissingOwnerReport = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205', '42703', 'PGRST204'].includes(error?.code) || /owner_reports|weekly_report|owner_report_data|set_weekly_report/i.test(error?.message || '');

const explain = (error, fallback) => (error?.code === '42501' ? 'No tienes permiso sobre este negocio (solo el dueño o un administrador).' : fallback);

// Reporte semanal al dueño: interruptor, número de avisos, ejemplo de la última semana e historial. Las cifras y las reglas viven en la base.
export const useOwnerReport = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [enabled, setEnabled] = useState(false);
  const [notifyPhone, setNotifyPhone] = useState('');
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [b, s, h] = await Promise.all([
      supabase.from('businesses').select('weekly_report, nombre_negocio').eq('id', business).maybeSingle(),
      supabase.from('billing_settings').select('notify_phone').eq('business_id', business).maybeSingle(),
      supabase.from('owner_reports').select('id, week_start, week_end, status, message, error, sent_at, data').eq('business_id', business).order('week_start', { ascending: false }).limit(8)
    ]);
    const error = b.error || h.error;
    if (error) {
      const gone = isMissingOwnerReport(error);
      setMissing(gone);
      if (!gone) toast.error(explain(error, 'No se pudo leer el reporte semanal.'));
      setHistory([]);
    } else {
      setMissing(false);
      setEnabled(Boolean(b.data?.weekly_report));
      setNotifyPhone(s.error ? '' : (s.data?.notify_phone || ''));
      setHistory(h.data || []);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const toggle = async (on) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('set_weekly_report', { p_business: String(activeBusinessId), p_on: Boolean(on) });
    if (error) { toast.error(explain(error, 'No se pudo cambiar el reporte semanal.')); return false; }
    setEnabled(Boolean(on));
    toast.success(on ? 'Listo: recibirás el resumen cada lunes' : 'Reporte semanal apagado');
    return true;
  };

  const saveNotifyPhone = async (raw) => {
    if (guardDemo()) return false;
    const text = String(raw ?? '').trim();
    const phone = text ? normalizeTestNumber(text) : null;
    if (text && !phone) { toast.error('El número no es válido: escribe el celular con indicativo (ej. 573016867636).'); return false; }
    const { error } = await supabase.from('billing_settings').upsert([{ business_id: String(activeBusinessId), notify_phone: phone }], { onConflict: 'business_id' });
    if (error) { toast.error(explain(error, 'No se pudo guardar el número de avisos.')); return false; }
    setNotifyPhone(phone || '');
    toast.success(phone ? 'Número de avisos guardado' : 'Sin número de avisos: no se enviará el reporte');
    return true;
  };

  // Resumen de la última semana completa, sin enviarlo.
  const showExample = async () => {
    setPreviewing(true);
    const { from, to } = lastWeekRange();
    const { data, error } = await supabase.rpc('owner_report_data', { p_business: String(activeBusinessId), p_from: from, p_to: to });
    setPreviewing(false);
    if (error) { toast.error(explain(error, 'No se pudo armar el ejemplo.')); return false; }
    setPreview(data);
    return true;
  };

  return { enabled, notifyPhone, history, preview, previewing, loading, missing, isReadOnlyDemo, reload: load, toggle, saveNotifyPhone, showExample, closeExample: () => setPreview(null) };
};
